'use client'

import { toCheckoutCart } from '@/feature/cart/services/cart.routing'
import { isLoggedInCartVisibleAtom } from '@/feature/cart/services/cart.store'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated, useIsPunchout } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { MiniCartButtonDocument, MiniCartButtonPunchoutDocument } from '@gql-generated/graphql'
import { useSetAtom } from 'jotai'
import { useIsMounted } from 'usehooks-ts'

import { Link } from '@/ui/links/link/Link.client'

gql(/* GraphQL */ `
  query MiniCartButton {
    currentUser {
      id
      checkout {
        cart(forceRefresh: false) {
          id
          ...MiniCartButton_Cart
        }
      }
    }
    config {
      checkout {
        isMiniCartEnabled
      }
    }
  }

  fragment MiniCartButton_Cart on Cart {
    id
    totalExclTax
  }

  query MiniCartButtonPunchout {
    currentUser {
      id
      punchout {
        cart {
          id
          totalExclTax
        }
      }
    }
  }
`)

export function MiniCartButton({ className }: WithClassName) {
  const isAuthenticated = useIsAuthenticated()
  const isPunchout = useIsPunchout()
  const { data, loading, error } = useQuery(MiniCartButtonDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    skip: !isAuthenticated || isPunchout,
  })
  const { data: punchoutData, loading: isLoadingPunchoutCart } = useQuery(
    MiniCartButtonPunchoutDocument,
    { ...APOLLO_CLIENT_SIDE_OPTIONS, skip: !isAuthenticated || !isPunchout },
  )

  const total = isPunchout
    ? (punchoutData?.currentUser?.punchout?.cart?.totalExclTax ?? 0)
    : (data?.currentUser?.checkout?.cart?.totalExclTax ?? 0)
  const isMiniCartEnabled = !isPunchout && (data?.config?.checkout?.isMiniCartEnabled ?? false)
  const { t } = useTranslation(['miniCartButton'])
  const setLoggedInCartAtomState = useSetAtom(isLoggedInCartVisibleAtom)

  const { pushWithProgressBar } = useRouter()

  const isMounted = useIsMounted()
  const desktopTitle = t('miniCartButton:title.desktop.count', { count: total })

  return (
    <>
      <div className="desktop:hidden">
        <Link
          bold
          variant="button-quaternary"
          size="lg"
          isRound
          iconLeft={<Icon.ShoppingBasket />}
          notification={total > 0}
          href={toCheckoutCart}
          className={className}
        >
          {t('miniCartButton:title.mobile')}
        </Link>
      </div>
      <div className="hidden desktop:block">
        <Button
          variant="primary"
          size="lg"
          iconLeft={<Icon.ShoppingBasket />}
          trackingData={{
            event: 'gaEvent',
            eventCategory: 'header',
            eventAction: 'clic connecté',
            eventLabel: 'panier',
          }}
          onPress={
            isMiniCartEnabled
              ? () => setLoggedInCartAtomState(true)
              : () => pushWithProgressBar(toCheckoutCart)
          }
          isLoading={isMounted() && (loading || isLoadingPunchoutCart)}
          isDisabled={isMiniCartEnabled && !!error?.graphQLErrors?.length}
          className={className}
        >
          {desktopTitle}
        </Button>
      </div>
    </>
  )
}

export default MiniCartButton
